import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  text?: string;
}

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
};

export function LoadingSpinner({
  size = "md",
  className,
  text,
}: LoadingSpinnerProps) {
  return (
    <div className="flex flex-col items-center justify-center space-y-2">
      <Loader2
        className={cn(
          "animate-spin text-muted-foreground",
          sizeClasses[size],
          className
        )}
      />
      {text && (
        <p className="text-sm text-muted-foreground animate-pulse">{text}</p>
      )}
    </div>
  );
}

interface PageLoadingProps {
  text?: string;
  className?: string;
}

export function PageLoading({ text, className }: PageLoadingProps) {
  const { t } = useTranslation();
  const content = text ?? t("loading.page");

  return (
    <div
      className={cn(
        "flex items-center justify-center min-h-[400px]",
        className
      )}
    >
      <LoadingSpinner size="lg" text={content} />
    </div>
  );
}

interface InlineLoadingProps {
  text?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function InlineLoading({
  text,
  size = "sm",
  className,
}: InlineLoadingProps) {
  const { t } = useTranslation();
  const message = text ?? t("loading.default");

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <Loader2 className={cn("animate-spin", sizeClasses[size])} />
      {message && (
        <span className="text-sm text-muted-foreground">{message}</span>
      )}
    </div>
  );
}
